import {
  addDecimal,
  cmpDecimal,
  isDecimal,
  mulDecimal,
  roundDecimalString,
  subDecimal,
} from '@biovet/ui';
import {
  defaultVoucherDate,
  hasFiscalCalendar,
  findAccount,
  hasSubAccounts,
  resolvePeriod,
  splitPostingAccount,
  subAccountsOf,
  type Period,
} from './reference-data.js';
import type { Side, VoucherTypeConfig } from './voucher-config.js';

/**
 * The voucher entry model: state, derivation and validation.
 *
 * Every rule enforced here is a rule the database enforces too. That is the
 * point — `packages/db/migrations/0015_invariants.sql` is the authority, and a
 * form that guesses differently either blocks a legal voucher or lets the
 * accountant fill in forty lines before the `COMMIT` throws. Each check below
 * names the trigger it mirrors.
 *
 * A voucher is a date, a narration, one party and its lines. Currency, rate and
 * branch are session settings chosen in the top bar and passed in — they are not
 * fields on the document, and `0019_simplify_ledger.sql` removed the columns that
 * pretended cost centre, zone and business line were.
 *
 * Money stays a decimal string from the keystroke to the GL preview. Base
 * amounts are computed with `mulDecimal` then rounded once at 4 dp, which is
 * `to_base(amount, rate)` — `round(amount * rate, 4)` — digit for digit.
 */

export type VoucherStatus = 'NEW' | 'DRAFT' | 'PENDING_APPROVAL' | 'APPROVED' | 'POSTED';
export type ApprovalState = 'NOT_REQUIRED' | 'PENDING' | 'APPROVED';
/**
 * An ISO 4217 code. Not a union — Setup → Currencies extends the `currency_code`
 * enum, so what is postable is decided by the register, not by this file.
 */
export type Currency = string;
/** The three roles the top bar switches between, until CASL exists. */
export type Role = 'accountant' | 'manager' | 'owner';

/** Chosen once in the top bar, read by every entry screen. */
export interface FxContext {
  currency: Currency;
  /** base = txn × rate. Always '1' on the base currency. */
  fxRate: string;
  /**
   * The book's reporting currency, from `/api/context`.
   *
   * Carried rather than assumed to be 'PKR': the base currency is a company
   * setting that Setup can change while no voucher has posted, and every
   * "is this line foreign?" test below pivots on it. Hard-coding 'PKR' made
   * those tests silently wrong for any other book — and would have called a
   * base-currency line foreign, warning that it "leaves no foreign-currency
   * balance to revalue" about the currency the balance is kept in.
   */
  baseCurrency: Currency;
}

export interface VoucherLineDraft {
  id: string;
  /**
   * What the accountant picked in the Account column.
   *
   * Not necessarily what posts. When this account has been given 5th-level
   * detail it is a group head, and the line lands on `subAccountCode` instead —
   * see `postingAccountOf`.
   */
  accountCode: string;
  /**
   * The 5th-level account under `accountCode` that the line actually posts to.
   *
   * Empty when the chosen account is a leaf and takes the posting itself. When
   * the account is a head, this is required: the database refuses a line on a
   * non-leaf, so a blank here is not a posting with a missing detail, it is no
   * posting at all.
   */
  subAccountCode: string;
  narration: string;
  /** Transaction currency, exactly as typed. Empty string means untouched. */
  debit: string;
  credit: string;
  /**
   * Which customer or supplier this line settles against.
   *
   * The older half of the same question. Where a control account has been broken
   * out into 5th-level sub-accounts, `subAccountCode` answers "whose" and this
   * stays empty; where it has not, this still does the work — `1131 Customers`
   * says the line is a receivable, and the party says whose. Both are offered in
   * the one Sub account column, because to the person entering a voucher they
   * are the same act.
   */
  partyCode: string;
}

/**
 * The account a line will actually post to.
 *
 * One rule, in one place, because three callers need to agree on it: the GL
 * preview the accountant approves, the payload sent to the API, and the
 * validation that decides whether Post is allowed. `trg_voucher_lines_derive`
 * refuses any line whose account is not a leaf, so a head with sub-accounts
 * posts through the chosen child or does not post.
 */
export function postingAccountOf(line: VoucherLineDraft): string {
  return line.subAccountCode || line.accountCode;
}

export interface VoucherDraft {
  voucherNo: string | null;
  voucherDate: string;
  narration: string;
  /** The cash or bank account on a CONTRA voucher. Unused for a JV. */
  contraAccountCode: string;
  lines: VoucherLineDraft[];
  status: VoucherStatus;
  approvalState: ApprovalState;
}

let lineSeq = 0;

export function emptyLine(): VoucherLineDraft {
  lineSeq += 1;
  return {
    id: `line-${lineSeq}`,
    accountCode: '',
    subAccountCode: '',
    narration: '',
    debit: '',
    credit: '',
    partyCode: '',
  };
}

/** A blank voucher, dated in the open period so the first thing a user sees is postable. */
export function emptyDraft(today = defaultVoucherDate()): VoucherDraft {
  return {
    voucherNo: null,
    voucherDate: today,
    narration: '',
    // Left empty on purpose: the chart of accounts arrives from the API, so the
    // screen picks the default cash or bank account once it has one.
    contraAccountCode: '',
    lines: [emptyLine(), emptyLine(), emptyLine()],
    status: 'NEW',
    approvalState: 'NOT_REQUIRED',
  };
}

/**
 * Clone as a new draft — "Copy / Duplicate" in business flow §3.1.
 *
 * The number is dropped rather than reused: the series is gapless and a number,
 * once consumed, belongs to the voucher that took it even if that voucher is
 * later cancelled.
 */
export function duplicateDraft(draft: VoucherDraft): VoucherDraft {
  return {
    ...draft,
    voucherNo: null,
    status: 'NEW',
    approvalState: 'NOT_REQUIRED',
    voucherDate: defaultVoucherDate(),
    lines: draft.lines.map((line) => ({ ...line, id: emptyLine().id })),
  };
}

/** Which line field a CONTRA voucher's single amount column writes to. */
export function amountFieldFor(config: VoucherTypeConfig): 'debit' | 'credit' {
  return config.analysisSide === 'Dr' ? 'debit' : 'credit';
}

// ── Amounts ──────────────────────────────────────────────────────────────────

/**
 * What the user typed, as a decimal string.
 *
 * Grouping separators are stripped because accountants type them, and a bare
 * `'.'` or `'-'` mid-keystroke is treated as empty rather than as an error —
 * validation that fires on the first character trains people to ignore it.
 */
export function parseAmount(raw: string): { value: string | null; invalid: boolean } {
  const text = raw.replace(/[\s,]/g, '');
  if (text === '' || text === '-' || text === '.' || text === '-.') {
    return { value: null, invalid: false };
  }
  if (!isDecimal(text)) return { value: null, invalid: true };
  return { value: text, invalid: false };
}

/** `to_base(amount, rate)` from `0002_utility.sql`: round(amount x rate, 4). */
export function toBaseExact(amountTxn: string, fxRate: string): string {
  return roundDecimalString(mulDecimal(amountTxn, fxRate), 4);
}

// ── Derivation ───────────────────────────────────────────────────────────────

export interface GlLine {
  lineNo: number;
  accountCode: string;
  side: Side;
  amountTxn: string;
  amountBase: string;
  /** The header party, stamped on the lines whose account keeps a sub-ledger. */
  partyCode: string;
  narration: string;
  /** True for the cash/bank line the form generates rather than the user typing. */
  generated: boolean;
}

interface EnteredLine {
  line: VoucherLineDraft;
  debit: string | null;
  credit: string | null;
  debitBase: string;
  creditBase: string;
}

export interface VoucherSummary {
  /** Lines the user filled in, with base amounts, in grid order. */
  entered: EnteredLine[];
  /** The posting as it will reach `voucher_lines`, contra line included. */
  glLines: GlLine[];
  totalDebitTxn: string;
  totalCreditTxn: string;
  totalDebitBase: string;
  totalCreditBase: string;
  /** Debit minus credit, in base. Zero is the only postable value. */
  differenceBase: string;
  differenceTxn: string;
  balanced: boolean;
  /** How many lines carry an amount. */
  filledLines: number;
  /**
   * A CONTRA voucher in foreign currency splits its contra line one-per-analysis
   * line when a single rolled-up line would round differently — see the comment
   * on `buildContraLines`.
   */
  contraSplit: boolean;
}

export function summarise(
  draft: VoucherDraft,
  config: VoucherTypeConfig,
  fx: FxContext,
): VoucherSummary {
  const rate = isDecimal(fx.fxRate) ? fx.fxRate : '1';

  const entered: EnteredLine[] = draft.lines.map((line) => {
    const debit = parseAmount(line.debit).value;
    const credit = parseAmount(line.credit).value;
    return {
      line,
      debit,
      credit,
      debitBase: debit ? toBaseExact(debit, rate) : '0',
      creditBase: credit ? toBaseExact(credit, rate) : '0',
    };
  });

  const filled = entered.filter((e) => e.debit !== null || e.credit !== null);

  const analysisLines: GlLine[] = filled.map((e, i) => {
    // The posting lands on the sub-account when there is one. The grid's own
    // Account column is a navigation aid past that point, not a destination.
    const posting = postingAccountOf(e.line);
    return {
      lineNo: i + 1,
      accountCode: posting,
      side: e.debit !== null ? 'Dr' : 'Cr',
      amountTxn: (e.debit ?? e.credit) as string,
      amountBase: e.debit !== null ? e.debitBase : e.creditBase,
      partyCode: partyFor(posting, e.line.partyCode),
      // The line's own narration and nothing else. The header's narration
      // belongs to the *document* — it prints on the voucher — and copying it
      // down made every GL line of a forty-line voucher read the same sentence,
      // which is worse than a blank: it looks like forty explanations and is one.
      narration: e.line.narration,
      generated: false,
    } satisfies GlLine;
  });

  const contra = config.contra
    ? buildContraLines(draft, config, filled)
    : { lines: [] as GlLine[], split: false };

  const all = [...analysisLines, ...contra.lines];
  // A voucher prints debits first; the generated contra line follows its own side.
  const ordered = [
    ...all.filter((l) => l.side === 'Dr'),
    ...all.filter((l) => l.side === 'Cr'),
  ].map((l, i) => ({ ...l, lineNo: i + 1 }));

  const totalDebitTxn = sumWhere(ordered, 'Dr', 'amountTxn');
  const totalCreditTxn = sumWhere(ordered, 'Cr', 'amountTxn');
  const totalDebitBase = sumWhere(ordered, 'Dr', 'amountBase');
  const totalCreditBase = sumWhere(ordered, 'Cr', 'amountBase');

  return {
    entered,
    glLines: ordered,
    totalDebitTxn,
    totalCreditTxn,
    totalDebitBase,
    totalCreditBase,
    differenceBase: subDecimal(totalDebitBase, totalCreditBase),
    differenceTxn: subDecimal(totalDebitTxn, totalCreditTxn),
    balanced: cmpDecimal(totalDebitBase, totalCreditBase) === 0 && ordered.length > 0,
    filledLines: filled.length,
    contraSplit: contra.split,
  };
}

/**
 * `voucher_lines.party_id` belongs on the lines that keep a sub-ledger and
 * nowhere else.
 *
 * A party typed against 6400 Rent would make the party ledger nonsense, so it is
 * dropped rather than trusted. The screen only offers the field on lines that
 * can take one; this is the guard for a line whose account is changed afterwards.
 */
function partyFor(accountCode: string, partyCode: string): string {
  if (!partyCode) return '';
  return findAccount(accountCode)?.requiresParty ? partyCode : '';
}

/**
 * The cash or bank side of a CONTRA voucher — one line per analysis line.
 *
 * It used to roll up into a single line and split only when the arithmetic
 * forced it. It no longer does, for a reason that is about the ledger rather
 * than about rounding: a Cash Payment settling four suppliers is four payments
 * out of the till, and rolling them into one credit line means the cash account
 * shows a single figure that reconciles to nothing anybody paid. Posting Paid
 * from / Received into line by line makes each one traceable to the analysis
 * line it answers, and each carries that line's own narration.
 *
 * The rounding case it used to guard is handled by construction now. The
 * database computes every line's base as `round(amount x rate, 4)`
 * independently, so `round(SUM(amount) x rate, 4)` and `SUM(round(amount x
 * rate, 4))` can differ by a fraction of a paisa per line — pairing each contra
 * line with its analysis line makes the two bases identical, and the voucher
 * balances exactly whatever the rate is.
 */
function buildContraLines(
  draft: VoucherDraft,
  config: VoucherTypeConfig,
  filled: EnteredLine[],
): { lines: GlLine[]; split: boolean } {
  const contra = config.contra;
  if (!contra || filled.length === 0) return { lines: [], split: false };

  const amountOf = (e: EnteredLine) => (e.debit ?? e.credit) as string;
  const baseOf = (e: EnteredLine) => (e.debit !== null ? e.debitBase : e.creditBase);

  return {
    lines: filled.map((e) => ({
      accountCode: draft.contraAccountCode,
      side: contra.side,
      partyCode: '',
      generated: true,
      lineNo: 0,
      amountTxn: amountOf(e),
      amountBase: baseOf(e),
      narration: e.line.narration,
    })),
    split: filled.length > 1,
  };
}

function sumWhere(lines: GlLine[], side: Side, key: 'amountTxn' | 'amountBase'): string {
  return lines.filter((l) => l.side === side).reduce((acc, l) => addDecimal(acc, l[key]), '0');
}

// ── Validation ───────────────────────────────────────────────────────────────

export type IssueSeverity = 'error' | 'warning';

export interface Issue {
  severity: IssueSeverity;
  message: string;
  /** Header field key, for highlighting. */
  field?: string;
  lineId?: string;
  /** The database object that enforces the same rule, shown in the panel. */
  enforcedBy?: string;
}

export interface Validation {
  issues: Issue[];
  errors: Issue[];
  warnings: Issue[];
  period: Period | undefined;
  /** Base total of the voucher, used for the approval threshold. */
  amountBase: string;
  requiresApproval: boolean;
  canSaveDraft: boolean;
  canSubmit: boolean;
  canPost: boolean;
  /** Why posting is blocked, in the order the user should fix things. */
  postBlockers: string[];
}

// ── The wire ─────────────────────────────────────────────────────────────────

export interface VoucherPayloadLine {
  accountCode: string;
  side: Side;
  amount: string;
  partyCode?: string;
  narration?: string;
}

export interface VoucherPayload {
  type: string;
  voucherNo: string | null;
  voucherDate: string;
  narration: string;
  partyCode: string | null;
  currency: Currency;
  fxRate: string;
  branchCode: string | null;
  lines: VoucherPayloadLine[];
}

/**
 * The draft, as `POST /api/vouchers` wants it.
 *
 * The lines sent are `summary.glLines` — the derived posting, contra line
 * included — not the rows in the grid. That is deliberate: the GL effect panel
 * renders the same array, so what the accountant is shown before pressing Post
 * is byte for byte what the server is asked to write. Sending the grid instead
 * would leave the contra line to be re-derived on the server, which is a second
 * implementation of `buildContraLines` waiting to disagree with this one.
 *
 * Amounts go in transaction currency. The database computes every base amount
 * itself in `trg_voucher_lines_derive`, and a base supplied from here would only
 * be a number for it to overwrite.
 */
export function toPayload(
  type: string,
  draft: VoucherDraft,
  summary: VoucherSummary,
  fx: FxContext,
  branchCode: string | null,
): VoucherPayload {
  return {
    type,
    voucherNo: draft.voucherNo,
    voucherDate: draft.voucherDate,
    narration: draft.narration,
    // The header carries no party. `vouchers.party_id` stays empty and each
    // line names its own, which is the only way one voucher can settle two
    // customers — a receipt covering two invoices does exactly that.
    partyCode: null,
    currency: fx.currency,
    fxRate: fx.currency === fx.baseCurrency ? '1' : fx.fxRate,
    branchCode,
    lines: summary.glLines.map((line) => ({
      accountCode: line.accountCode,
      side: line.side,
      amount: line.amountTxn,
      partyCode: line.partyCode || undefined,
      narration: line.narration || undefined,
    })),
  };
}

/** One line as it came back from the API. */
export interface LoadedLine {
  accountCode: string;
  side: Side;
  amount: string;
  partyCode: string | null;
  narration: string | null;
}

/** A voucher as it came back from the API, reduced to what the form needs. */
export interface LoadedVoucher {
  voucherNo: string;
  voucherDate: string;
  status: string;
  narration: string | null;
  partyCode: string | null;
  lines: LoadedLine[];
}

/**
 * Turn a saved voucher back into a draft the form can render.
 *
 * On a CONTRA voucher the split is unambiguous without inspecting the chart:
 * every analysis line sits on `analysisSide` and every generated cash or bank
 * line on the opposite side, because that is what `buildContraLines` did on the
 * way in. A JV has no split — every line goes to the grid on the side it was
 * posted.
 *
 * The status comes back as the database has it, so a POSTED voucher loads
 * locked and a DRAFT loads editable, with no second source of truth.
 */
export function draftFromLoaded(loaded: LoadedVoucher, config: VoucherTypeConfig): VoucherDraft {
  const contraSide = config.contra?.side;
  const contraLines = contraSide ? loaded.lines.filter((l) => l.side === contraSide) : [];
  const analysis = contraSide ? loaded.lines.filter((l) => l.side !== contraSide) : loaded.lines;

  return {
    voucherNo: loaded.voucherNo,
    voucherDate: loaded.voucherDate,
    narration: loaded.narration ?? '',
    contraAccountCode: contraLines[0]?.accountCode ?? '',
    lines:
      analysis.length > 0
        ? analysis.map((line) => ({
            ...emptyLine(),
            // The ledger holds one account per line — the leaf that was posted
            // to. The grid edits a head and a sub-account, so a level-5 code
            // has to be put back into the two columns it was entered in.
            ...splitPostingAccount(line.accountCode),
            narration: line.narration ?? '',
            debit: line.side === 'Dr' ? line.amount : '',
            credit: line.side === 'Cr' ? line.amount : '',
            partyCode: line.partyCode ?? '',
          }))
        : [emptyLine()],
    status: normaliseStatus(loaded.status),
    approvalState: loaded.status === 'PENDING_APPROVAL' ? 'PENDING' : 'NOT_REQUIRED',
  };
}

/**
 * `voucher_status` has six values; this form knows five.
 *
 * REVERSED and CANCELLED both mean "posted and then undone" as far as the entry
 * screen is concerned: the content is frozen and nothing more can be added. They
 * map to POSTED so every lock keyed on it keeps working, and the register beside
 * the form is where the real status is shown.
 */
function normaliseStatus(status: string): VoucherStatus {
  switch (status) {
    case 'DRAFT':
    case 'PENDING_APPROVAL':
    case 'APPROVED':
    case 'POSTED':
      return status;
    default:
      return 'POSTED';
  }
}

export function validate(
  draft: VoucherDraft,
  config: VoucherTypeConfig,
  summary: VoucherSummary,
  role: Role,
  fx: FxContext,
): Validation {
  const issues: Issue[] = [];
  const period = resolvePeriod(draft.voucherDate);
  const error = (message: string, extra: Partial<Issue> = {}) =>
    issues.push({ severity: 'error', message, ...extra });
  const warn = (message: string, extra: Partial<Issue> = {}) =>
    issues.push({ severity: 'warning', message, ...extra });

  // ── Date and period ─────────────────────────────────────────────────────────
  // The period is not a field — it is derived, exactly as resolve_period() does
  // it. It only reaches the user when it blocks them.
  if (!draft.voucherDate) {
    error('Voucher date is required.', { field: 'voucherDate' });
  } else if (!hasFiscalCalendar()) {
    // The calendar has not arrived, or there is no open year at all. Either way
    // the date cannot be judged yet, and guessing would block a legal voucher.
    error('No open financial year. Open one under Setup → Financial Year & Periods.', {
      field: 'voucherDate',
      enforcedBy: 'resolve_period()',
    });
  } else if (!period) {
    // Judged against the periods, not against one year's dates. More than one
    // financial year is open whenever next year has been opened before this one
    // is closed — which is how January posts — and testing the date against
    // "the" year refused every legal voucher in the other one.
    error(
      `Nothing open covers ${draft.voucherDate}. Open the financial year it falls in under Setup → Financial Year & Periods.`,
      { field: 'voucherDate', enforcedBy: 'resolve_period()' },
    );
  } else if (period.status === 'CLOSED') {
    error(
      `${period.name} is closed. A draft may sit in a closed period; posting into one is refused.`,
      { field: 'voucherDate', enforcedBy: 'trg_vouchers_period_lock' },
    );
  } else if (period.status === 'FUTURE') {
    error(`${period.name} has not been opened yet.`, {
      field: 'voucherDate',
      enforcedBy: 'trg_vouchers_period_lock',
    });
  }

  // ── The session rate ────────────────────────────────────────────────────────
  // Chosen in the top bar, so a bad value is not this form's fault — but it is
  // this form's problem, because every base amount comes from it.
  if (!isDecimal(fx.fxRate) || cmpDecimal(fx.fxRate, '0') <= 0) {
    error('The FX rate in the top bar is not a positive number — fix it there.', {
      field: 'fxRate',
    });
  }

  // ── Cash or bank account ────────────────────────────────────────────────────
  // The side is fixed by the voucher type and the form does not offer it. Which
  // account is still a real choice: there are three banks and three cash tills.
  if (config.contra) {
    const account = findAccount(draft.contraAccountCode);
    if (!draft.contraAccountCode) {
      error(`${config.contra.label} is required.`, { field: 'contraAccountCode' });
    } else if (!account) {
      error('Unknown account.', { field: 'contraAccountCode' });
    } else if (!account.isLeaf) {
      error(`${account.code} is a group account — a posting must target a leaf.`, {
        field: 'contraAccountCode',
        enforcedBy: 'trg_voucher_lines_derive',
      });
    } else if (account.control !== config.contra.kind) {
      error(`${account.code} is not a ${config.contra.kind.toLowerCase()} account.`, {
        field: 'contraAccountCode',
      });
    } else if (
      account.fcyCurrency &&
      fx.currency !== account.fcyCurrency &&
      fx.currency !== fx.baseCurrency
    ) {
      error(
        `${account.code} holds ${account.fcyCurrency} — a ${fx.currency} line cannot post to it.`,
        { field: 'contraAccountCode', enforcedBy: 'trg_voucher_lines_derive' },
      );
    } else if (!account.fcyCurrency && fx.currency !== fx.baseCurrency) {
      warn(
        `${account.code} is a ${fx.baseCurrency} account. A ${fx.currency} entry posts its base equivalent and leaves no foreign-currency balance to revalue.`,
        { field: 'contraAccountCode' },
      );
    }
  }

  if (!draft.narration.trim()) {
    warn(
      'Narration is empty. It heads the printed voucher — the General Ledger shows each line’s own narration instead.',
      { field: 'narration' },
    );
  }

  // ── Lines ───────────────────────────────────────────────────────────────────
  const minimum = config.shape === 'JOURNAL' ? 2 : 1;
  if (summary.filledLines < minimum) {
    error(
      minimum === 2
        ? 'A journal voucher needs at least one debit and one credit line.'
        : 'Enter at least one line.',
    );
  }

  const seen = new Set<string>();

  for (const entry of summary.entered) {
    const { line, debit, credit } = entry;
    const hasAmount = debit !== null || credit !== null;
    const touched = hasAmount || line.accountCode !== '' || line.narration !== '';
    if (!touched) continue;

    if (parseAmount(line.debit).invalid || parseAmount(line.credit).invalid) {
      error('Amount is not a number.', { lineId: line.id });
      continue;
    }

    if (!line.accountCode) {
      error('Account is required on every line.', { lineId: line.id });
    } else if (hasSubAccounts(line.accountCode) && !line.subAccountCode) {
      // The head was chosen but the detail was not. Naming the count makes the
      // message actionable — "pick one of 240" is a different instruction from
      // "pick one of 2".
      const count = subAccountsOf(line.accountCode).length;
      error(
        `${line.accountCode} has ${count} sub-account${count === 1 ? '' : 's'}, so it takes no lines of its own — choose the one this posts to in the Sub account column.`,
        { lineId: line.id, enforcedBy: 'trg_voucher_lines_derive' },
      );
    } else {
      // Everything below judges the account that will actually be written.
      const account = findAccount(postingAccountOf(line));
      if (!account) {
        error('Unknown account.', { lineId: line.id });
      } else {
        if (!account.isLeaf) {
          error(
            `${account.code} ${account.name} is a group account — a posting must target a leaf.`,
            { lineId: line.id, enforcedBy: 'trg_voucher_lines_derive' },
          );
        }
        if (config.shape === 'JOURNAL' && account.control && !account.allowDirectPosting) {
          error(
            `${account.code} ${account.name} is a control account — post through its sub-ledger document, not a JV.`,
            { lineId: line.id, enforcedBy: 'trg_voucher_lines_derive' },
          );
        }
        if (account.requiresParty && !line.partyCode) {
          error(
            `${account.code} ${account.name} keeps a sub-ledger, so this line needs a party — otherwise the balance cannot be reconciled to a customer or supplier.`,
            { lineId: line.id },
          );
        }
        if (
          account.fcyCurrency &&
          fx.currency !== account.fcyCurrency &&
          fx.currency !== fx.baseCurrency
        ) {
          error(
            `${account.code} holds ${account.fcyCurrency} — a ${fx.currency} line cannot post to it.`,
            { lineId: line.id, enforcedBy: 'trg_voucher_lines_derive' },
          );
        }
      }
    }

    if (!hasAmount) {
      error('Line has an account but no amount.', { lineId: line.id });
    } else if (debit !== null && credit !== null) {
      error('A line is either a debit or a credit, never both.', { lineId: line.id });
    } else if (cmpDecimal((debit ?? credit) as string, '0') <= 0) {
      error('Amount must be greater than zero — put it on the other side instead of negating it.', {
        lineId: line.id,
      });
    }

    // Compared on the posting account: two lines against 1131 that name
    // different customers are two different sub-accounts and not a duplicate,
    // which is exactly the case a receipt covering two invoices makes.
    const posting = postingAccountOf(line);
    if (hasAmount && posting) {
      if (seen.has(posting)) {
        warn('Same account as an earlier line — merge them or give each a narration.', {
          lineId: line.id,
        });
      }
      seen.add(posting);
    }
  }

  // A line with no narration of its own now reaches the General Ledger blank —
  // the header's is no longer copied down. Said once, with a count, rather than
  // once per row: three blank rows on a new voucher are not three problems.
  const unnarrated = summary.entered.filter(
    (e) => (e.debit !== null || e.credit !== null) && !e.line.narration.trim(),
  ).length;
  if (unnarrated > 0) {
    warn(
      `${unnarrated} line${unnarrated === 1 ? '' : 's'} carry no narration, so ${unnarrated === 1 ? 'it reaches' : 'they reach'} the General Ledger with none — the header's narration heads the voucher and is not copied down.`,
    );
  }

  // ── Balance ─────────────────────────────────────────────────────────────────
  if (summary.filledLines >= minimum && !summary.balanced) {
    if (config.shape === 'JOURNAL' && cmpDecimal(summary.differenceTxn, '0') === 0) {
      error(
        `Sides agree in ${fx.currency} but differ by ${summary.differenceBase} in base: each line converts and rounds to 4 dp independently. Adjust a line so the base totals meet.`,
        { enforcedBy: 'trg_vouchers_balance (DEFERRABLE)' },
      );
    } else {
      error(`Debits and credits differ by ${summary.differenceBase} in base currency.`, {
        enforcedBy: 'trg_vouchers_balance (DEFERRABLE)',
      });
    }
  }

  // ── Gate ────────────────────────────────────────────────────────────────────
  const errors = issues.filter((i) => i.severity === 'error');
  const warnings = issues.filter((i) => i.severity === 'warning');
  const amountBase = summary.totalDebitBase;
  const overThreshold = cmpDecimal(amountBase, config.approvalThreshold) > 0;
  const requiresApproval = overThreshold && role === 'accountant';

  const postBlockers: string[] = [];
  if (errors.length > 0) {
    postBlockers.push(`${errors.length} validation ${errors.length === 1 ? 'error' : 'errors'}`);
  }
  if (period && period.status !== 'OPEN') postBlockers.push(`${period.name} is not open`);
  if (requiresApproval) {
    postBlockers.push('above your approval threshold — submit it for a checker');
  }
  if (draft.status === 'POSTED') postBlockers.push('already posted');

  return {
    issues,
    errors,
    warnings,
    period,
    amountBase,
    requiresApproval,
    canSaveDraft: draft.status !== 'POSTED' && summary.filledLines > 0,
    canSubmit: draft.status !== 'POSTED' && errors.length === 0 && requiresApproval,
    canPost: postBlockers.length === 0 && summary.filledLines > 0,
    postBlockers,
  };
}
